/* eslint-disable react/no-unused-prop-types,no-console,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import ContentLoader from 'react-content-loader';
import { withTranslation } from 'react-i18next';
import { formatRoute } from 'react-router-named-routes';
import SweetAlert from 'react-bootstrap-sweetalert';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// material-ui icons
import Agency from '@material-ui/icons/SupervisorAccountRounded';
import AddIco from '@material-ui/icons/AddCircle';
import CancelIco from '@material-ui/icons/Cancel';
import LinearProgress from '@material-ui/core/LinearProgress';
import Refresh from '@material-ui/icons/Refresh';
import FilterIco from '@material-ui/icons/FilterList';
// material-ui/ core components
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import withStyles from '@material-ui/core/styles/withStyles';
import { Hidden } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// global variables
import { creditBundlesStatus } from 'config';
// our components
import { CREDIT_BUNDLE, USER, PROMOTED_AD } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import { LoadingAnimation } from 'components/common/Loading';
import Paginator from 'components/common/Paginator';
import { TableLoader } from 'components/common/TableLoader';
import RedirectFlashMessage from 'components/common/RedirectFlashMessage';
import WithPermission, {
  checkPermissions,
} from 'components/User/WithPermission';
import Filter from 'components/CreditBundle/Filter';
import { defaultFilters } from 'components/CreditBundle/List';
import qs from 'helpers/queryStrings';

const translateValidationSchema = t =>
  Yup.object({
    ad: Yup.string().required(t('errors.required')),
  });

/**
 * Initial values to AdForm
 */
const initialValues = {
  ad: '',
  bundle: null,
};

/**
 * @class
 * @extends React.Component
 */
class CreditBundlesList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      fields: {
        ...initialValues,
      },
      showFilters: false,
      showAddAdToBundle: false,
      showUpdateBundle: false,
      showCancelConfirm: false,
      cancelBundleId: null,
      adId: null,
      bundleId: null,
    };
  }

  /**
   * @description
   * Handle show collapsed or full filters.
   */
  handleShowFilters = () => {
    const { showFilters } = this.state;
    this.setState({ showFilters: !showFilters });
  };

  /**
   * @description
   * Redirect to credit bundle/create route.
   */
  handleCreate = () => {
    const history = this.context;
    history.push(CREDIT_BUNDLE.CREATE);
  };

  /**
   * @description
   * Redirect to user/details route.
   *
   * @param {string} id - User id.
   */
  handleDetailsUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  /**
   * @description
   * Redirect to paidad/create route.
   * @param {Object} ad - Ad id.
   * @param {Object} bundle - Bundle id.
   */
  handleCreatePaidAd = (ad, bundle) => {
    const history = this.context;
    const urlString = qs.stringify({
      ad,
      bundle,
    });
    history.push({ pathname: PROMOTED_AD.CREATE, search: urlString });
  };

  /**
   * @description
   * Handle cancel credit bundle.
   * @param {string} bundleId - Bundle ID to cancel.
   */
  handleCancelBundle = bundleId => {
    this.setState({
      showCancelConfirm: true,
      cancelBundleId: bundleId,
    });
  };

  /**
   * @description
   * Confirm cancel credit bundle.
   */
  confirmCancelBundle = () => {
    const { cancelCreditBundle } = this.props;
    const { cancelBundleId } = this.state;

    cancelCreditBundle(cancelBundleId, errors => {
      this.setState({
        showCancelConfirm: false,
        cancelBundleId: null,
      });

      if (errors && errors.length > 0) {
        // Handle error - could show notification here
        console.error('Error canceling bundle:', errors);
      }
    });
  };

  /**
   * @description
   * Render Status cell.
   *
   * @param {Object} node - An ad.
   * @returns {*} Status cell.
   */
  renderCellStatus = node => {
    const { classes, t } = this.props;

    switch (node.status) {
      case creditBundlesStatus.CREATED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.pendingTag}`}>
            {t(`credit_bundles.list.status.${creditBundlesStatus.CREATED}`)}
          </h6>
        );
      case creditBundlesStatus.STARTED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.runningTag}`}>
            {t(`credit_bundles.list.status.${creditBundlesStatus.STARTED}`)}
          </h6>
        );
      case creditBundlesStatus.COMPLETED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.completedTag}`}>
            {t(`credit_bundles.list.status.${creditBundlesStatus.COMPLETED}`)}
          </h6>
        );
      case creditBundlesStatus.EXPIRED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.deletedTag}`}>
            {t(`credit_bundles.list.status.${creditBundlesStatus.EXPIRED}`)}
          </h6>
        );
      case creditBundlesStatus.CANCELLED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.deletedTag}`}>
            {t(`credit_bundles.list.status.${creditBundlesStatus.CANCELLED}`)}
          </h6>
        );
      default:
        break;
    }
    return null;
  };

  /**
   * @description
   * Description of ReactTable columns.
   *
   * @returns {Array} Table columns.
   */
  renderColumns = () => {
    const { me, classes, t } = this.props;
    const hasPermission = checkPermissions(me, ['cancel_creditbundle']);
    const columns = [
      {
        Header: (
          <span className={classes.headerField}>
            {t('credit_bundles.list.headers.id')}
          </span>
        ),
        accessor: 'id',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('credit_bundles.list.headers.price')}
          </span>
        ),
        accessor: 'price',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('credit_bundles.list.headers.commission')}
          </span>
        ),
        accessor: 'commission',
        sortable: false,
        filterable: false,
        minWidth: 50,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('credit_bundles.list.headers.paidads')}</p>
          </span>
        ),
        accessor: 'paidads',
        sortable: false,
        filterable: false,
        minWidth: 50,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('credit_bundles.list.headers.add_ad')}</p>
          </span>
        ),
        accessor: 'add',
        sortable: false,
        filterable: false,
        minWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('credit_bundles.list.headers.owner')}
          </span>
        ),
        accessor: 'owner',
        sortable: false,
        filterable: false,
        minWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('credit_bundles.list.headers.status')}</p>
          </span>
        ),
        accessor: 'status',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('credit_bundles.list.headers.actions')}</p>
          </span>
        ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        expander: true,
        show: false,
      },
    ];
    if (!hasPermission) {
      return columns.filter(col => col.accessor !== 'actions');
    }

    return columns;
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Function} t - Translation function.
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table rows.
   */
  renderRows = (t, edges) => {
    const { fields } = this.state;
    return edges.map(({ node }) => ({
      id: node.id,
      price: (
        <span data-cy="price">{`${
          node.secondaryPrice ? node.secondaryPrice : node.price
        } ${
          node.secondaryCurrency ? node.secondaryCurrency : node.currency
        }`}</span>
      ),
      commission: (
        <span data-cy="price">{`${
          node.rate ? node.rate * node.commission : node.commission
        } ${
          node.secondaryCurrency ? node.secondaryCurrency : node.currency
        }`}</span>
      ),
      paidads: (
        <div style={{ textAlign: 'center' }}>
          <span data-cy="price">
            <a
              rel="noopener noreferrer"
              href={`${PROMOTED_AD.LIST}?promotedBundle=${node.id}`}
            >
              {`${node.paidads.edges.length}/5`}
            </a>
          </span>
        </div>
      ),
      add: (
        <div style={{ textAlign: 'center' }}>
          <Button
            disabled={
              node.paidads.edges.length >= 5 ||
              node.status === creditBundlesStatus.CANCELLED
            }
            color="primary"
            simple
            onClick={() => {
              this.setState({
                showAddAdToBundle: true,
              });
              this.setState({
                fields: {
                  ...fields,
                  bundle: node.id,
                },
              });
            }}
          >
            <AddIco />
          </Button>
        </div>
      ),
      owner: (
        <WithPermission
          alt={
            node.user && (
              <div>
                <Agency />
                {node.user.fullname}
              </div>
            )
          }
          allowPermissions={['view_useragency', 'view_ownership_useragency']}
        >
          {node.user &&
            (node.user.id !== '0' ? (
              <div id={`bundle-owner-${node.user.id}`}>
                <Button
                  data-cy="paidAdOwner"
                  simple
                  color="primary"
                  style={{ margin: 0 }}
                  onClick={() => this.handleDetailsUser(node.user.id)}
                >
                  <Agency />
                  {node.user.fullname}
                </Button>
              </div>
            ) : (
              <div id={`bundle-owner-${node.user.fakeId}`}>
                {`${t('promoted_ad.form.fields.private_user')} (${
                  node.user.fakeId
                })`}
              </div>
            ))}
        </WithPermission>
      ),
      status: <span data-cy="status">{this.renderCellStatus(node)}</span>,
      actions: (
        <div style={{ textAlign: 'center' }}>
          <Button
            disabled={node.status !== creditBundlesStatus.CREATED}
            color="danger"
            simple
            onClick={() => this.handleCancelBundle(node.id)}
            data-cy="cancelBundle"
          >
            <CancelIco />
          </Button>
        </div>
      ),
    }));
  };

  /**
   * @description
   * Render Filter, Paginator and ReactTable components.
   * @returns {*} Filter, Paginator and ReactTable components.
   */
  render() {
    const {
      data,
      loading,
      fullLoading,
      changeNumberOfRows,
      changePage,
      numberOfRowsSelected,
      paginationDirection,
      classes,
      filter,
      restartFilters,
      appliedFilter,
      refresh,
      checkAdWithBundle,
      updateBundle,
      t,
    } = this.props;
    const {
      fields,
      showFilters,
      showAddAdToBundle,
      showUpdateBundle,
      showCancelConfirm,
      adId,
      bundleId,
    } = this.state;
    const edges = loading || data === undefined ? [] : data.creditBundles.edges;
    const rows = this.renderRows(t, edges);
    const cols = this.renderColumns(edges);
    const validationSchema = translateValidationSchema(t);
    return (
      <React.Fragment>
        <RedirectFlashMessage />
        <GridContainer>
          <GridItem xs={12}>
            <div className={classes.pageContainer}>
              <GridContainer justify="space-between" alignItems="center">
                <GridItem>
                  <h3 className={classes.title}>
                    {t('credit_bundles.list.title')}
                  </h3>
                </GridItem>
                <SweetAlert
                  title={`Bundle: ${fields.bundle}`}
                  showConfirm={false}
                  showCancel={false}
                  onConfirm={() => {}}
                  customClass={classes.adModalConfirmation}
                  show={showAddAdToBundle}
                  closeOnClickOutside
                  onCancel={() =>
                    this.setState({
                      showAddAdToBundle: !showAddAdToBundle,
                    })
                  }
                >
                  <Formik
                    ref={ref => {
                      this.formikRef = ref;
                    }}
                    initialValues={fields}
                    validationSchema={validationSchema}
                    onSubmit={values => this.handleSubmit(values)}
                    render={({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleSubmit,
                      handleBlur,
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <div
                          className={classes.adFormContainer}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 16,
                          }}
                        >
                          <TextField
                            label={t('promoted_ad.form.fields.id')}
                            name="ad"
                            error={touched.ad && !!errors.ad}
                            helperText={touched.ad ? errors.ad : ''}
                            InputProps={{
                              value: values.ad,
                              onChange: e => handleChange(e),
                              onBlur: handleBlur,
                              type: 'number',
                            }}
                          />
                          <div>
                            <Button
                              id="get-ad"
                              color="primary"
                              simple
                              disabled={!values.ad || !!errors.ad || loading}
                              onClick={() => {
                                checkAdWithBundle(
                                  values.ad,
                                  values.bundle,
                                  error => {
                                    if (error) {
                                      switch (error.code) {
                                        case 'bundle-can-not-be-updated':
                                        case 'invalid-ad-bundle':
                                          this.formikRef.setErrors({
                                            ad: t(
                                              'credit_bundles.form.not_compatible',
                                            ),
                                          });
                                          break;
                                        case 'no-available-packages':
                                          this.formikRef.setErrors({
                                            ad: t(
                                              'credit_bundles.form.no_available_packages',
                                            ),
                                          });
                                          break;
                                        case 'bundle-can-be-updated':
                                          this.setState({
                                            showUpdateBundle: true,
                                            adId: values.ad,
                                            bundleId: values.bundle,
                                          });
                                          break;
                                        default:
                                          this.formikRef.setErrors({
                                            ad: error.message,
                                          });
                                          break;
                                      }
                                    } else {
                                      this.handleCreatePaidAd(
                                        values.ad,
                                        values.bundle,
                                      );
                                    }
                                  },
                                );
                              }}
                            >
                              {t('credit_bundles.form.add_ad')}
                            </Button>{' '}
                            <Button
                              id="cancel-create"
                              simple
                              color="danger"
                              onClick={() => {
                                this.setState({
                                  showAddAdToBundle: !showAddAdToBundle,
                                });
                              }}
                            >
                              {t('common.cancel')}
                            </Button>
                          </div>
                        </div>
                      </form>
                    )}
                  />
                </SweetAlert>
                <SweetAlert
                  title={t('credit_bundles.form.can_be_updated')}
                  showConfirm={false}
                  showCancel={false}
                  onConfirm={() => {}}
                  customClass={classes.adModalConfirmation}
                  show={showUpdateBundle}
                  closeOnClickOutside
                  onCancel={() =>
                    this.setState({
                      showUpdateBundle: !showUpdateBundle,
                    })
                  }
                >
                  <div>
                    <Button
                      id="get-ad"
                      color="primary"
                      simple
                      onClick={() => {
                        updateBundle(adId, bundleId, () => {
                          this.setState({
                            showUpdateBundle: false,
                            adId: null,
                            bundleId: null,
                          });
                          this.handleCreatePaidAd(adId, bundleId);
                        });
                      }}
                    >
                      {t('credit_bundles.form.update_bundle')}
                    </Button>{' '}
                    <Button
                      id="cancel-create"
                      simple
                      color="danger"
                      onClick={() => {
                        this.setState({
                          showUpdateBundle: !showUpdateBundle,
                        });
                      }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </SweetAlert>
                {showCancelConfirm && (
                  <SweetAlert
                    warning
                    style={{ display: 'block', padding: 30 }}
                    title=""
                    onConfirm={() => {}}
                    showConfirm={false}
                  >
                    <h4>{t('credit_bundles.list.cancel_confirm_title')}</h4>
                    <p>{t('credit_bundles.list.cancel_confirm_message')}</p>
                    <GridContainer
                      style={{ marginTop: 20, padding: '0 20px' }}
                      justify="space-between"
                    >
                      <GridItem>
                        <Button
                          simple
                          color="primary"
                          onClick={() =>
                            this.setState({
                              showCancelConfirm: false,
                              cancelBundleId: null,
                            })
                          }
                        >
                          {t('common.cancel')}
                        </Button>
                      </GridItem>
                      <GridItem>
                        <Button
                          data-cy="confirmCancelBundle"
                          color="danger"
                          onClick={this.confirmCancelBundle}
                        >
                          {t('credit_bundles.list.cancel_confirm')}
                        </Button>
                      </GridItem>
                    </GridContainer>
                  </SweetAlert>
                )}
                <GridItem>
                  <Button
                    color="primary"
                    simple
                    className={classes.showFilters}
                    onClick={() => refresh()}
                    id="refresh"
                    key="refresh"
                    style={{
                      marginRight: 24,
                    }}
                  >
                    <Refresh />
                    {t('common.refresh')}
                  </Button>
                  <WithPermission
                    allowPermissions={[
                      'add_creditbundle',
                      'add_ownership_creditbundle',
                    ]}
                  >
                    <Button
                      data-cy="createCreditBundle"
                      color="success"
                      default
                      onClick={this.handleCreate}
                      id="createCreditBundle"
                    >
                      {t('credit_bundles.create')}
                    </Button>
                  </WithPermission>
                </GridItem>
              </GridContainer>

              <GridContainer>
                <GridItem xs={12}>
                  <div className={classes.contentContainer}>
                    <div
                      className={[
                        classes.loadingLineContainer,
                        classes.topLine,
                      ].join(' ')}
                    >
                      {' '}
                      {fullLoading && (
                        <LinearProgress
                          classes={{
                            barColorPrimary: classes.loadingLine,
                          }}
                        />
                      )}
                    </div>
                    <GridContainer
                      justify="space-between"
                      alignItems="center"
                      className={classes.loteActionsAnimation}
                    >
                      <GridItem xs={12} sm={8} md={7}>
                        <div className={classes.searchFieldContainer}>
                          <Button
                            color="info"
                            round
                            simple={
                              !showFilters &&
                              JSON.stringify(appliedFilter) ===
                                JSON.stringify(defaultFilters)
                            }
                            // className={classes.filterBtn}
                            onClick={this.handleShowFilters}
                            id="filter"
                            key="filter"
                            data-cy="filterBtn"
                          >
                            <FilterIco />
                            <span>
                              {JSON.stringify(appliedFilter) ===
                              JSON.stringify(defaultFilters)
                                ? t('user.list.filter')
                                : t('user.list.filtering')}
                            </span>
                          </Button>
                        </div>
                        <Hidden
                          only={['sm', 'md', 'lg', 'xl']}
                          implementation="css"
                        >
                          <GridContainer>
                            <GridItem xs={12}>
                              <div
                                className={`${classes.loteActionsAnimation}
                    ${
                      showFilters
                        ? classes.showPaidAdFilters
                        : classes.hideFilters
                    }`}
                              >
                                <Filter
                                  filter={filter}
                                  appliedFilter={appliedFilter}
                                  restartFilters={restartFilters}
                                  showFilters={this.handleShowFilters}
                                />
                              </div>
                            </GridItem>
                          </GridContainer>
                        </Hidden>
                      </GridItem>
                      <GridItem xs={12} sm={4} md={5}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={data.creditBundles.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                    <Hidden only={['xs']} implementation="css">
                      <GridContainer>
                        <GridItem xs={12}>
                          <div
                            className={`${classes.loteActionsAnimation}
                    ${
                      showFilters
                        ? classes.showPaidAdFilters
                        : classes.hideFilters
                    }`}
                          >
                            <Filter
                              filter={filter}
                              appliedFilter={appliedFilter}
                              restartFilters={restartFilters}
                              showFilters={this.handleShowFilters}
                            />
                          </div>
                        </GridItem>
                      </GridContainer>
                    </Hidden>
                    <GridContainer>
                      <GridItem xs={12}>
                        <div>
                          {loading ? (
                            <TableLoader />
                          ) : (
                            <ReactTable
                              data={rows}
                              filterable
                              columns={cols}
                              loading={loading}
                              pageSize={
                                edges.length < numberOfRowsSelected
                                  ? edges.length
                                  : numberOfRowsSelected
                              }
                              showPagination={false}
                              className={[
                                '-striped -highlight',
                                classes.tbl,
                              ].join(' ')}
                              defaultFilterMethod={(field, row) => {
                                const id = field.pivotId || field.id;
                                return row[id] !== undefined
                                  ? String(row[id]).startsWith(field.value)
                                  : true;
                              }}
                              SubComponent={() => (
                                <div className={classes.loading}>
                                  <LoadingAnimation />
                                </div>
                              )}
                              getTrProps={(state, rowInfo) => ({
                                style: {
                                  backgroundColor: rowInfo && '',
                                  borderLeft: rowInfo && '',
                                },
                              })}
                              noDataText={t('credit_bundles.list.no_data')}
                              minRows={8}
                            />
                          )}
                        </div>
                      </GridItem>
                    </GridContainer>
                    <GridContainer justify="flex-end">
                      <GridItem xs={12} sm={4}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={data.creditBundles.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              id="Combined-Shape"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * CreditBundlesList PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop ads query's results
 * @param itemProcessing - id of items that is processing
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param appliedFilter - prop pass to Filter
 * @param loading - prop ads query or mutations status
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with string translations
 * @param failedIds - prop from HOC with string translations
 * @param notification - prop to know if notification will be showed or not
 * @callback packageUpdate - callback to update elements
 * @callback packageDisable - callback to disable elements
 * @callback changePage - prop pass to Paginator
 * @callback changeNumberOfRows - prop pass to Paginator
 * @callback filter - prop pass to Filter
 * @callback restartFilters - prop pass to Filter
 * @callback createCreditBundlePage - prop pass to List
 * @callback refresh - callback to refresh page
 * @callback closeNotification - callback to close notification
 */

CreditBundlesList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  itemProcessing: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.shape({}).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  // eslint-disable-next-line react/require-default-props
  paginationDirection: PropTypes.string,
  packageUpdate: PropTypes.func.isRequired,
  packageDisable: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
  refresh: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  notification: PropTypes.instanceOf(Object).isRequired,
  closeNotification: PropTypes.func.isRequired,
  createCreditBundlePage: PropTypes.func.isRequired,
  checkAdWithBundle: PropTypes.func.isRequired,
  updateBundle: PropTypes.func.isRequired,
  cancelCreditBundle: PropTypes.func.isRequired,
  me: PropTypes.instanceOf(Object).isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PromotedAdPackageWrapper = props => <CreditBundlesList {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(PromotedAdPackageWrapper),
);
